import React, { useState, useEffect } from 'react';
import { 
  Activity, Clock, User, FileText, CheckSquare, Image, Briefcase,
  Calendar, MessageSquare, Settings, Loader2, Filter, RefreshCw,
  ChevronRight, Search, BarChart3, TrendingUp
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const RECORD_TYPES = {
  task: { icon: CheckSquare, color: 'bg-blue-500/20 text-blue-400', label: 'Task' },
  content: { icon: FileText, color: 'bg-purple-500/20 text-purple-400', label: 'Content' },
  asset: { icon: Image, color: 'bg-green-500/20 text-green-400', label: 'Asset' },
  partnership: { icon: Briefcase, color: 'bg-orange-500/20 text-orange-400', label: 'Partnership' },
  calendar: { icon: Calendar, color: 'bg-cyan-500/20 text-cyan-400', label: 'Calendar' },
  message: { icon: MessageSquare, color: 'bg-pink-500/20 text-pink-400', label: 'Message' },
  blueprint: { icon: Settings, color: 'bg-yellow-500/20 text-yellow-400', label: 'Blueprint' },
  system: { icon: Settings, color: 'bg-gray-500/20 text-gray-400', label: 'System' }
};

const ACTION_LABELS = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  approved: 'Approved',
  rejected: 'Rejected',
  submitted: 'Submitted',
  completed: 'Completed',
  workspace_created: 'Workspace Created',
  brand_blueprint_created: 'Blueprint Created',
  task_created: 'Task Created',
  task_updated: 'Task Updated',
  content_approve: 'Content Approved',
  content_reject: 'Content Rejected',
  content_request_changes: 'Changes Requested',
  asset_created: 'Asset Uploaded',
  asset_deleted: 'Asset Deleted'
};

const AEAuditLog = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (userEmail) {
      fetchAuditLog();
      fetchStats();
    }
  }, [userEmail, filterType]);

  const fetchAuditLog = async () => {
    try {
      let url = `${API}/api/always-elevate/audit-log?user_email=${encodeURIComponent(userEmail)}&limit=100`;
      if (filterType !== 'all') {
        url += `&record_type=${filterType}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setEntries(data.data);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/audit-log/stats?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const filteredEntries = entries.filter(entry => {
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      return (
        entry.action?.toLowerCase().includes(searchLower) ||
        entry.record_type?.toLowerCase().includes(searchLower) ||
        entry.user_email?.toLowerCase().includes(searchLower) ||
        JSON.stringify(entry.details || {}).toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-audit-log-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ELLE MAXWELL AGENCY</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-audit-log-title">
                Audit Log
              </h1>
              <p className="text-gray-400 text-sm mt-1">Track all workspace activity</p>
            </div>
            <button
              onClick={() => { fetchAuditLog(); fetchStats(); }}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="refresh-audit-btn"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-[#C4A052]/50 hover:scale-[1.02]">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-5 h-5 text-[#C4A052]" />
                <TrendingUp className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-2xl font-bold">{stats.recent_count}</p>
              <p className="text-xs text-gray-400">Activity (7 days)</p>
            </div>
            
            {stats.by_type?.slice(0, 3).map(item => {
              const recordType = RECORD_TYPES[item._id] || RECORD_TYPES.system;
              const RecordIcon = recordType.icon;
              return (
                <div key={item._id} className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-[#C4A052]/50 hover:scale-[1.02]">
                  <div className="flex items-center justify-between mb-2">
                    <RecordIcon className={`w-5 h-5 ${recordType.color.split(' ')[1]}`} />
                  </div>
                  <p className="text-2xl font-bold">{item.count}</p>
                  <p className="text-xs text-gray-400 capitalize">{item._id} Events</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            {[
              { id: 'all', label: 'All Activity' },
              { id: 'task', label: 'Tasks' },
              { id: 'content', label: 'Content' },
              { id: 'asset', label: 'Assets' },
              { id: 'partnership', label: 'Partnerships' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilterType(f.id)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filterType === f.id 
                    ? 'bg-[#C4A052] text-black font-medium' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search activity..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-[#C4A052] w-64"
              data-testid="audit-search"
            />
          </div>
        </div>

        {/* Activity Timeline */}
        {filteredEntries.length === 0 ? (
          <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
            <Activity className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No Activity Found</h3>
            <p className="text-gray-500 text-sm">
              {filterType !== 'all' ? 'No activity for this filter' : 'Activity will appear here as you use the workspace'}
            </p>
          </div>
        ) : (
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="audit-log-list">
            <div className="divide-y divide-white/5">
              {filteredEntries.map((entry, idx) => {
                const recordType = RECORD_TYPES[entry.record_type] || RECORD_TYPES.system;
                const RecordIcon = recordType.icon;
                const actionLabel = ACTION_LABELS[entry.action] || entry.action?.replace(/_/g, ' ');
                
                return (
                  <div key={entry.id || idx} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`p-2.5 rounded-xl ${recordType.color} flex-shrink-0`}>
                        <RecordIcon className="w-5 h-5" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-medium">
                              <span className="capitalize">{actionLabel}</span>
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                              <User className="w-3 h-3" />
                              <span>{entry.user_email?.split('@')[0] || 'System'}</span>
                              <span className="text-gray-600">•</span>
                              <span className="capitalize">{entry.record_type}</span>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {getTimeAgo(entry.created_at)}
                          </span>
                        </div>
                        
                        {/* Details */}
                        {entry.details && Object.keys(entry.details).length > 0 && (
                          <div className="mt-2 bg-white/5 rounded-lg p-2 text-xs text-gray-400">
                            {Object.entries(entry.details).map(([key, value]) => (
                              <div key={key} className="flex gap-2">
                                <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                                <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* User Activity Breakdown */}
        {stats?.by_user && stats.by_user.length > 0 && (
          <div className="mt-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#C4A052]" />
              Activity by User
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stats.by_user.map(item => (
                <div key={item._id} className="bg-black border border-white/20 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#C4A052]/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#C4A052]" />
                    </div>
                    <div>
                      <p className="font-medium">{item._id?.split('@')[0] || 'Unknown'}</p>
                      <p className="text-xs text-gray-400">{item._id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">{item.count}</p>
                    <p className="text-xs text-gray-400">actions</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AEAuditLog;
